import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { useState } from "react";
import { handleApiError } from "@/utils/errors";
import { catchError } from "@/lib/errors";
import { toast } from "sonner";
import DotPattern from "@/components/dot-pattern";
import AboutForm from "@/features/about/components/form/about-form";
import { aboutFormSchema } from "@/features/about/components/form/schemas";
import { aboutRepo } from "@/lib/repositories";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTES } from "@/constants/routes";
import { useTranslation } from "react-i18next";
import { queryClient } from "@/lib/query-client";
import { Skeleton } from "@/components/ui/skeleton";
import { useFetchAbout } from "@/features/about/hooks/useFetchAbout";
import { useDynamicPageTitle } from "@/hooks/useDynamicPageTitle";
import type { ApiAbout } from "@/features/about/types";

type AboutEditFormContentProps = { about: ApiAbout };

const AboutEditFormContent = ({ about }: AboutEditFormContentProps) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof aboutFormSchema>>({
    resolver: zodResolver(aboutFormSchema),
    defaultValues: {
      title: about.title,
      description: about.description,
      body_simple: about.body_simple,
      body_wysiwyg: about.body_wysiwyg,
      icon_name: about.icon_name,
      order: about.order,
      is_active: about.is_active,
    },
  });

  async function onSubmit(data: z.infer<typeof aboutFormSchema>) {
    if (!id) return;
    setIsSubmitting(true);
    const [error, aboutData] = await catchError(aboutRepo.update(id, data));
    if (error) toast.error(handleApiError(error).message);
    if (aboutData) {
      await queryClient.invalidateQueries({ queryKey: ["abouts"] });
      toast.success(t("messages.about_edit_success"));
      navigate(ROUTES.ABOUT);
    }
    setIsSubmitting(false);
  }

  return (
    <AboutForm
      form={form}
      onSubmit={onSubmit}
      isLoading={isSubmitting}
      about={about}
    />
  );
};

const AboutEditPage = () => {
  const { id } = useParams();
  const { about, isLoading } = useFetchAbout(id);
  useDynamicPageTitle(about ? about.title : "");

  return (
    <div className="relative flex flex-col items-center gap-y-3 p-3 py-4 sm:py-10 sm:p-6">
      <DotPattern />
      {isLoading && <Skeleton className="w-full h-137 md:h-80" />}
      {about && <AboutEditFormContent about={about} />}
    </div>
  );
};

export default AboutEditPage;
